import { useMemo, useState, useEffect, useRef } from 'react';
import { useApi } from '../api.js';
import { supabase } from '../supabaseClient.js';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from 'recharts';
import { ClickableValue } from './DrillDown.jsx';
import DataTable from './datatable/DataTable.jsx';

const TIER_LABELS = {
  small: 'Small (<10m³)',
  regular: 'Regular (10m³)',
  oversize18: 'Oversize 18m³',
  oversize28: 'Oversize 28m³',
  unknown: 'Unknown plate'
};

const DOW_FULL = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function fyForDate(d) { return d.getMonth() >= 5 ? d.getFullYear() + 1 : d.getFullYear(); }
function fyForMonth(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  return m >= 6 ? y + 1 : y;
}

function fmtIsoDate(d) {
  const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,'0'); const day = String(d.getDate()).padStart(2,'0');
  return y + '-' + m + '-' + day;
}
function isoDateMinusDays(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return fmtIsoDate(d);
}
function dowFromIso(iso) {
  if (!iso) return '';
  const [y,m,d] = iso.split('-').map(Number);
  return DOW_FULL[new Date(y, m-1, d).getDay()];
}

function heatColor(avg, max) {
  if (!avg || avg <= 0) return { bg: '#f8fafc', fg: '#cbd5e1' };
  const ratio = max > 0 ? avg / max : 0;
  if (ratio >= 0.85) return { bg: '#0a8b3a', fg: '#fff' };
  if (ratio >= 0.65) return { bg: '#22a14a', fg: '#fff' };
  if (ratio >= 0.45) return { bg: '#65c489', fg: '#0f172a' };
  if (ratio >= 0.25) return { bg: '#bbe5c8', fg: '#14532d' };
  return { bg: '#e3f3e9', fg: '#166534' };
}

function Heatmap({ heatmap }) {
  const buckets = (heatmap && heatmap.buckets) || [];
  const days = (heatmap && heatmap.days) || [];
  const max = days.reduce((m, d) => d.cells.reduce((mm, c) => Math.max(mm, c.avg), m), 0);
  if (!days.length || max === 0) {
    return <div className="note" style={{padding:'1rem'}}>Not enough data yet to chart historical busy hours.</div>;
  }
  let peak = { dow: null, bucket: null, avg: 0 };
  for (const d of days) for (const c of d.cells) {
    if (c.avg > peak.avg) {
      const bk = buckets.find(b => b.key === c.bucket);
      peak = { dow: d.label, bucket: bk ? bk.label : c.bucket, avg: c.avg };
    }
  }
  return (
    <>
      <table className="heatmap-grid">
        <thead>
          <tr>
            <th></th>
            {buckets.map(b => <th key={b.key} className="num">{b.label}</th>)}
            <th className="num">Daily avg</th>
          </tr>
        </thead>
        <tbody>
          {days.map(d => (
            <tr key={d.dow}>
              <td className="dow-cell">{d.label}<div className="small">{d.daysObserved} days</div></td>
              {d.cells.map(c => {
                const col = heatColor(c.avg, max);
                const bk = buckets.find(b => b.key === c.bucket);
                const title = d.label + ' ' + (bk ? bk.label : c.bucket) + ' — avg ' + c.avg + ' loads (' + c.total + ' total over ' + d.daysObserved + ' days)';
                return (
                  <td key={c.bucket} className="num heatmap-cell" style={{ backgroundColor: col.bg, color: col.fg }} title={title}>
                    {c.avg > 0 ? c.avg.toFixed(1) : '·'}
                  </td>
                );
              })}
              <td className="num" style={{ fontWeight: 600, background: '#f9fafb' }}>{d.dailyAvg}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {peak.dow && (
        <div className="note" style={{marginTop:'0.5rem'}}>
          Peak window: <strong>{peak.dow} {peak.bucket}</strong> — averaging <strong>{peak.avg.toFixed(1)}</strong> loads.
        </div>
      )}
    </>
  );
}

// Customer × time-of-day heatmap — shows when each customer arrives.
// Color intensity = % of that customer's loads falling in each bucket.
function CustomerTimeHeatmap({ data, fy }) {
  const buckets = (data && data.buckets) || [];
  const rows = (data && data.rows) || [];
  if (buckets.length === 0 || rows.length === 0) return null;
  return (
    <div className="chart-card" style={{padding: 0, marginTop: '1rem'}}>
      <div style={{ padding: '0.8rem 1rem 0.4rem' }}>
        <h3 style={{margin:0}}>When customers arrive — last 90 days</h3>
        <div className="note">% of each customer's loads by time-of-day bucket. Darker = bigger share.</div>
      </div>
      <div style={{ overflowX: 'auto', padding: '0 1rem 1rem' }}>
        <table className="month-grid">
          <thead>
            <tr>
              <th className="sticky-col">Customer</th>
              {buckets.map(b => <th key={b.key} className="num">{b.label}</th>)}
              <th className="num">Total loads</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const peakPct = Math.max(...r.cells.map(c => c.pct), 0);
              return (
                <tr key={r.customer}>
                  <td className="sticky-col">
                    <ClickableValue query={{ fy, samePeriod: false, customer: r.customer }} title={r.customer + ' — last 90 days'}>
                      {r.customer}
                    </ClickableValue>
                  </td>
                  {r.cells.map((c, i) => {
                    const col = heatColor(c.pct, peakPct);
                    const style = col ? { backgroundColor: col.bg, color: col.fg } : {};
                    return (
                      <td key={i} className="num cell" style={style}>
                        {c.count > 0 ? (
                          <>
                            <strong>{c.count}</strong>
                            <span style={{opacity: 0.7, marginLeft: 4, fontSize: '0.85em'}}>
                              {Math.round(c.pct * 100)}%
                            </span>
                          </>
                        ) : <span style={{color:'#cbd5e1'}}>—</span>}
                      </td>
                    );
                  })}
                  <td className="num" style={{fontWeight: 600, background: '#f9fafb'}}>{r.total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function cellColor(curr, prev) {
  if (!curr && !prev) return null;
  if (curr === 0) return { bg: '#fdecec', fg: '#9b1c1c' };
  if (!prev || prev === 0) return { bg: '#e0f2fe', fg: '#075985' };
  const pct = ((curr - prev) / prev) * 100;
  if (pct >= 25)  return { bg: '#bbf7d0', fg: '#14532d' };
  if (pct >= 5)   return { bg: '#dcfce7', fg: '#166534' };
  if (pct > -5)   return { bg: '#f3f4f6', fg: '#374151' };
  if (pct > -25)  return { bg: '#fee2e2', fg: '#991b1b' };
  return                  { bg: '#fecaca', fg: '#7f1d1d' };
}

// Custom Recharts tooltip for the daily-loads chart — shows day of week + date + loads
function DailyTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '8px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '0.85rem' }}>
      <div style={{ fontWeight: 600 }}>{dowFromIso(row.date)}</div>
      <div style={{ color: '#666', fontSize: '0.78rem' }}>{row.date}</div>
      <div style={{ marginTop: 4 }}><strong>{row.loads}</strong> loads</div>
    </div>
  );
}


export default function TruckVolume() {
  const [topN, setTopN] = useState(20);
  // Weekly view for the per-customer grid (Suresh 2026-09-25)
  const [weeklyGrid, setWeeklyGrid] = useState(false);
  // Per-cell notes ("why was this week/month slow") — hover to read,
  // double-click a cell to add/edit/clear. Server-side, shared by all.
  const notesApi = useApi('/api/grid-notes');
  const noteMap = new Map(((notesApi.data && notesApi.data.notes) || []).map(n => [n.customer + '|' + n.period_key, n]));
  async function editCellNote(customer, periodKey, label) {
    const existing = noteMap.get(customer + '|' + periodKey);
    const txt = window.prompt(
      'Note for ' + customer + ' — ' + label + '\n(shown on hover; leave empty to remove)',
      existing ? existing.note : ''
    );
    if (txt === null) return;                       // cancelled
    try {
      const r = await fetch('/api/grid-notes', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer, periodKey, note: txt })
      });
      if (!r.ok) throw new Error(await r.text());
      notesApi.refresh();
    } catch (e) { alert('Note save failed: ' + e.message); }
  }
  const [selectedDate, setSelectedDate] = useState(fmtIsoDate(new Date())); // default = today

  const apiPath = '/api/trucks?date=' + selectedDate;
  const { data, error, loading, refresh } = useApi(apiPath);

  // Realtime auto-refresh: subscribe to tips_excel + tips_google
  // change events. Debounce 500ms so a bulk approve of 20 rows fires
  // ONE refresh instead of 20. Same pattern as Balances / Review
  // (QW5, 2026-07-08). Suresh 2026-07-11: truck volume is mission-
  // critical data that must feel live; polling every 5 min isn't
  // enough during peak hours.
  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);
  useEffect(() => {
    let timer = null;
    const scheduleRefresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { timer = null; refreshRef.current(); }, 500);
    };
    const channel = supabase
      .channel('trucks_live')
      // signal row bumped by a DB trigger on tip changes (no tip data via the public key — 2026-10-07)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'realtime_signals', filter: 'name=eq.tips' }, scheduleRefresh)
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, []);

  if (loading && !data) return <div className="loading">Loading truck data…</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const { summary = {}, tiers = {}, timeOfDay = [], daily = [], byCustomer = [], recent = [], monthly = {}, heatmap = { buckets: [], days: [] }, selectedDay = {}, customerTimeHeatmap = { buckets: [], rows: [] } } = data || {};
  const tierTotal = Object.values(tiers).reduce((s, v) => s + v, 0);
  const dayLabel = selectedDay.dow ? selectedDay.dow + ' ' + selectedDay.date : selectedDate;
  const fy = fyForDate(new Date());
  const weekly = (data && data.weekly) || {};
  const grid = weeklyGrid && (weekly.rows || []).length > 0 ? weekly : monthly;
  const months = grid.months || [];
  const monthlyRows = grid.rows || [];
  const colTotals = grid.colTotals || [];
  const visible = monthlyRows.slice(0, topN);

  // Drill-down query for the selected day specifically (just that one date)
  const qDay = { start: selectedDate, end: selectedDate, limit: 200 };

  function shiftDay(days) {
    const [y,m,d] = selectedDate.split('-').map(Number);
    const dt = new Date(y, m-1, d); dt.setDate(dt.getDate() + days);
    setSelectedDate(fmtIsoDate(dt));
  }
  const todayIso = fmtIsoDate(new Date());
  const yesterdayIso = isoDateMinusDays(1);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{margin:0}}>Truck Volume</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <button className="refresh-btn" onClick={() => shiftDay(-1)} title="Previous day">◀</button>
          <input type="date" className="refresh-btn" value={selectedDate}
                 max={todayIso}
                 onChange={(e) => setSelectedDate(e.target.value || todayIso)} />
          <button className="refresh-btn" onClick={() => shiftDay(+1)} disabled={selectedDate >= todayIso} title="Next day">▶</button>
          <button className="refresh-btn" onClick={() => setSelectedDate(yesterdayIso)} disabled={selectedDate === yesterdayIso}>Yesterday</button>
          <button className="refresh-btn" onClick={() => setSelectedDate(todayIso)} disabled={selectedDate === todayIso}>Today</button>
          <button className="refresh-btn" onClick={refresh}>Refresh</button>
        </div>
      </div>
      <div className="small" style={{marginTop:'0.2rem', marginBottom:'0.8rem'}}>
        Showing day-specific metrics for <strong>{dayLabel}</strong>. Other sections (last 30 days, monthly grid, heatmap) are not affected.
      </div>

      <div className="cards">
        <div className="card">
          <div className="label">Loads on {dayLabel}</div>
          <div className="value">
            <ClickableValue query={qDay} title={'Loads — ' + dayLabel}>{summary.loadsToday ?? 0}</ClickableValue>
          </div>
        </div>
        <div className="card">
          <div className="label">Loads that week</div>
          <div className="value">{summary.loadsWeek ?? 0}</div>
          <div className="sub">Mon-Sun containing the day</div>
        </div>
        <div className="card">
          <div className="label">Loads that month</div>
          <div className="value">{summary.loadsMonth ?? 0}</div>
          <div className="sub">avg {summary.avgLoadsPerDayMonth ?? 0}/day</div>
        </div>
        <div className="card">
          <div className="label">Badger that day / month</div>
          <div className="value">
            <ClickableValue query={{ ...qDay, customer: 'Badger' }} title={'Badger loads — ' + dayLabel}>
              {summary.badgerToday ?? 0} / {summary.badgerMonth ?? 0}
            </ClickableValue>
          </div>
          <div className="sub">avg {summary.badgerDailyAvg ?? 0}/working day</div>
        </div>
      </div>

      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
          <h3 style={{margin:0}}>Typical busy hours — last 90 days</h3>
          <span className="note" style={{margin:0}}>Avg loads per (day-of-week × hour bucket). Darker = busier.</span>
        </div>
        <Heatmap heatmap={heatmap} />
      </div>

      <div className="panel-row">
        <div className="panel">
          <h3>Loads by hour on {dayLabel}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={timeOfDay}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="loads" fill="#0a8b3a" />
            </BarChart>
          </ResponsiveContainer>
          <div className="note">Buckets match the daily flash report.</div>
        </div>

        <div className="panel">
          <h3>Loads by tier (last 30 days)</h3>
          <DataTable
            viewName="trucks.tier-breakdown"
            data={Object.entries(tiers).filter(([k, count]) => count > 0).map(([key, count]) => ({ key, label: TIER_LABELS[key] || key, count, share: tierTotal > 0 ? (count / tierTotal) * 100 : 0 }))}
            defaultSort={[{ id: 'count', desc: true }]}
            columns={[
              { id: 'label', accessorKey: 'label', header: 'Tier', size: 200 },
              { id: 'count', accessorKey: 'count', header: 'Loads', size: 100, meta: { align: 'right' },
                cell: ({ row }) => row.original.key === 'unknown'
                  ? row.original.count
                  : <ClickableValue query={{ fy, samePeriod: true, tier: row.original.key }} title={(row.original.label) + ' loads — current FY to date'}>{row.original.count}</ClickableValue> },
              { id: 'share', accessorKey: 'share', header: 'Share', size: 90, meta: { align: 'right' }, cell: ({ getValue }) => { const v = getValue(); return v > 0 ? v.toFixed(1) + '%' : '—'; } },
            ]}
          />
        </div>
      </div>

      <div className="chart-card">
        <h3>Daily loads (last 30 days)</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={daily}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip content={<DailyTooltip />} />
            <Line type="monotone" dataKey="loads" stroke="#0a8b3a" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>


      <div className="chart-card" style={{padding: 0}}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8rem 1rem 0.4rem' }}>
          <h3 style={{margin:0}}>{weeklyGrid ? 'Weekly loads per customer — last 13 weeks' : 'Monthly loads per customer — last 12 months'}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <label className="small" style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', userSelect: 'none' }}
                 title="Show the last 13 weeks (Monday-start) instead of the last 12 months">
            <input type="checkbox" checked={weeklyGrid} onChange={(e) => setWeeklyGrid(e.target.checked)} />
            Weekly
          </label>
          <select className="refresh-btn" value={topN} onChange={(e) => setTopN(parseInt(e.target.value, 10))}>
            <option value={10}>Top 10</option>
            <option value={20}>Top 20</option>
            <option value={50}>Top 50</option>
            <option value={9999}>All</option>
          </select>
          </div>
        </div>
        <div style={{ overflowX: 'auto', padding: '0 1rem 1rem' }}>
          <table className="month-grid">
            <thead>
              <tr>
                <th className="sticky-col">Customer</th>
                {months.map(m => <th key={m.key} className="num">{m.label}</th>)}
                <th className="num">{weeklyGrid ? '13-wk total' : '12-mo total'}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => (
                <tr key={r.customer}>
                  <td className="sticky-col">
                    <ClickableValue query={{ fy, samePeriod: false, customer: r.customer }} title={r.customer + ' — last 12 months'}>
                      {r.customer}
                    </ClickableValue>
                  </td>
                  {r.cells.map((cell, i) => {
                    const curr = cell.loads;
                    const prev = i > 0 ? r.cells[i-1].loads : null;
                    const col = cellColor(curr, prev);
                    const style = col ? { backgroundColor: col.bg, color: col.fg } : {};
                    const note = noteMap.get(r.customer + '|' + cell.month);
                    if (note) { style.boxShadow = 'inset 0 -2px 0 0 #6366f1'; style.cursor = 'help'; }
                    return (
                      <td key={cell.month} className="num cell" style={style}
                          title={(note ? '\ud83d\udcdd ' + note.note + '\n\u2014 ' + (note.updated_by || '') + '\n\n' : '') + 'Double-click to ' + (note ? 'edit' : 'add') + ' a note'}
                          onDoubleClick={() => editCellNote(r.customer, cell.month, cell.label)}>
                        {curr ? (
                          weeklyGrid
                            ? curr
                            : <ClickableValue query={{ fy: fyForMonth(cell.month), customer: r.customer }} title={r.customer + ' — ' + cell.label}>
                                {curr}
                              </ClickableValue>
                        ) : <span style={{color:'#cbd5e1'}}>—</span>}
                      </td>
                    );
                  })}
                  <td className="num" style={{fontWeight: 600, background: '#f9fafb'}}>{r.totalLoads}</td>
                </tr>
              ))}
              <tr style={{borderTop: '2px solid #94a3b8'}}>
                <td className="sticky-col" style={{fontWeight: 700}}>All customers</td>
                {colTotals.map(c => {
                  const note = noteMap.get('(all)|' + c.month);
                  return (
                    <td key={c.month} className="num"
                        style={{ fontWeight: 600, background: '#f9fafb', ...(note ? { boxShadow: 'inset 0 -2px 0 0 #6366f1', cursor: 'help' } : {}) }}
                        title={(note ? '\ud83d\udcdd ' + note.note + '\n\u2014 ' + (note.updated_by || '') + '\n\n' : '') + 'Double-click to ' + (note ? 'edit' : 'add') + ' a note for ALL customers this period'}
                        onDoubleClick={() => editCellNote('(all)', c.month, c.label + ' (all customers)')}>
                      {c.loads}
                    </td>
                  );
                })}
                <td className="num" style={{fontWeight: 700, background: '#e5e7eb'}}>{colTotals.reduce((s,c)=>s+c.loads,0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="note" style={{padding: '0 1rem 0.8rem'}}>
          Color: {weeklyGrid ? 'week-over-week' : 'month-over-month'} change for that customer.
          <span className="cell-legend" style={{background:'#bbf7d0', color:'#14532d'}}>▲ +25%+</span>
          <span className="cell-legend" style={{background:'#dcfce7', color:'#166534'}}>▲ +5–25%</span>
          <span className="cell-legend" style={{background:'#f3f4f6', color:'#374151'}}>flat (±5%)</span>
          <span className="cell-legend" style={{background:'#fee2e2', color:'#991b1b'}}>▼ −5 to −25%</span>
          <span className="cell-legend" style={{background:'#fecaca', color:'#7f1d1d'}}>▼ −25%+</span>
          <span className="cell-legend" style={{background:'#fdecec', color:'#9b1c1c'}}>zero</span>
          <span className="cell-legend" style={{background:'#e0f2fe', color:'#075985'}}>new</span>
          {' '}· Double-click any cell to add a note (purple underline = has note; hover to read).
        </div>
      </div>

      <CustomerTimeHeatmap data={customerTimeHeatmap} fy={fy} />

      <div className="panel-row">
        <div className="panel">
          <h3>By customer (last 30 days)</h3>
          <DataTable
            viewName="trucks.by-customer"
            data={byCustomer.slice(0, 15)}
            defaultSort={[{ id: 'loads', desc: true }]}
            columns={[
              { id: 'customer', accessorKey: 'customer', header: 'Customer', size: 240 },
              { id: 'loads', accessorKey: 'loads', header: 'Loads', size: 100, meta: { align: 'right' },
                cell: ({ row }) => <ClickableValue query={{ fy, samePeriod: true, customer: row.original.customer }} title={row.original.customer + ' — current FY to date'}>{row.original.loads}</ClickableValue> },
            ]}
          />
        </div>

        <div className="panel">
          <h3>Recent loads</h3>
          <DataTable
            viewName="trucks.recent-loads"
            data={recent.slice(0, 20)}
            defaultSort={[{ id: 'date', desc: true }]}
            columns={[
              { id: 'date', accessorKey: 'date', header: 'Date', size: 110 },
              { id: 'plate', accessorKey: 'licensePlate', header: 'Plate', size: 110 },
              { id: 'customer', accessorKey: 'resolvedCustomer', header: 'Customer', size: 240 },
              { id: 'tier', accessorKey: 'tier', header: 'Tier', size: 160, cell: ({ getValue }) => TIER_LABELS[getValue()] || '?' },
            ]}
          />
        </div>
      </div>
    </>
  );
}
